import { Container, FormFieldHeading } from '@/components/Elements';
import ApplicationHeading from '@/components/Elements/Heading/ApplicationHeading';
import {
  DatePickerField,
  InputField,
  ListOptionsField,
  RadioGroupField,
  SelectField,
} from '@/components/Form';
import FormMainLabel from '@/components/Elements/Heading/FormMainLabel';
import StepperFormLayout from '@/components/Layout/StepperFormLayout';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TAX_INCOME_OPTIONS } from '@/pages/tax/routes/TaxReturns/constant/optionList';
import { AGREE_OPTIONS } from '@/constant/options';
import { usePostLeadDataMutation } from '@/pages/onboarding/api/onboardAPI';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Grid,
  message,
  Row,
  theme,
  Typography,
} from 'antd';
import {
  useGetApplicationDetailQuery,
  usePostLodgeReturnMutation,
  usePutLodgeReturnMutation,
} from '@/pages/tax/apis/lodgeReturnAPI';
import { yupResolver } from '@hookform/resolvers/yup';
import { healthCheckSchema } from '@/pages/tax/routes/HealthCheck/schema/healthCheckSchema';
import { HealthCheckFormType } from './types';
import { useFetchUserDataQuery } from '@/store/apis/userApi';
import dayjs from 'dayjs';
const { useBreakpoint } = Grid;
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import PhoneInputField from '@/components/Form/PhoneInputField';
import { useCountryOptions } from '@/hooks/useCountryOptions';
import { PlusOutlined } from '@ant-design/icons';
import { useGetPersonalPropertyListQuery } from '@/pages/mortgage/apis/propertyAPI';
import PropertyItems from '@/pages/mortgage/components/Property/PropertyItems';
import PropertyFormModal from '@/pages/property/components/PropertyFormModal';
import refetchPropertyData from '@/pages/mortgage/apis/propertyAPI';
import HealthCheckMeetingModal from '../../components/healthCheckMeetingModal/HealthCheckMeetingModal';

const IntroContent = () => (
  <>
    <Typography.Title level={3} style={{ margin: '0 0 1.5rem 0' }}>
      Not sure where you stand with the ATO? Don't leave it to chance.
    </Typography.Title>

    <div
      style={{
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '1.5rem',
      }}
    >
      <Typography.Paragraph
        style={{ fontSize: '16px', marginBottom: '1.5rem' }}
      >
        As an Australian expat, your tax obligations don't disappear — even if
        you've been away for years or have no Australian income. A quick tax
        health check can reveal any issues, missed lodgements, or risks of
        penalties before they become costly problems.
      </Typography.Paragraph>

      <Typography.Paragraph style={{ fontSize: '16px', marginBottom: '0' }}>
        At Odin, we offer a <strong>free tax health check</strong> to help you
        stay compliant and in control. We'll access your ATO record, review your
        history, and provide a clear, personalised action plan — whether you
        need to lodge past returns or confirm that everything's on track.
      </Typography.Paragraph>
    </div>

    <Typography.Title level={3}>
      Enter your details to get a free Australian tax health check
    </Typography.Title>
  </>
);

const HealthCheck = () => {
  const { id } = useParams();
  const total = 2;
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const countryOptions = useCountryOptions();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<HealthCheckFormType>({
    resolver: yupResolver(healthCheckSchema),
  });

  const { data: profileData } = useFetchUserDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: applicationDetail } = useGetApplicationDetailQuery(
    {
      application_id: id!,
    },
    { skip: !id, refetchOnMountOrArgChange: true },
  );

  const { data: userData } = useFetchUserDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: propertyData, refetch: refetchPropertyData } =
    useGetPersonalPropertyListQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const [postLodgeReturn, { isLoading: postLodgeReturnLoading }] =
    usePostLodgeReturnMutation();

  const [putLodgeReturn, { isLoading: putLodgeReturnLoading }] =
    usePutLodgeReturnMutation();

  const [selectedProperties, setSelectedProperties] = useState<
    (string | number)[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [australianPropertyData, setAustralianPropertyData] = useState<any[]>(
    [],
  );

  const handlePropertySelect = (propertyId: number) => {
    setSelectedProperties((prevSelected) => {
      const updatedSelection = prevSelected.includes(propertyId)
        ? prevSelected.filter((id) => id !== propertyId)
        : [...prevSelected, propertyId];

      setValue('propertyIds', updatedSelection);
      return updatedSelection;
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const [healthCheckMeetingModalOpen, setHealthCheckMeetingModalOpen] =
    useState(false);

  const handleHealthCheckMeetingModalClose = () => {
    setHealthCheckMeetingModalOpen(false);
    navigate('/dashboard', { replace: true });
  };

  const handleHealthCheckMeetingModalOk = () => {
    navigate('/dashboard', { replace: true });
  };

  const handleNewPropertyAdded = (newProperty: any) => {
    setSelectedProperties((prevSelected) => {
      const updatedSelection = [...prevSelected, newProperty.id];
      setValue('propertyIds', updatedSelection);
      return updatedSelection;
    });
  };

  const onSubmit = (data: any) => {
    const agreeToTerms = getValues('agreeToTerms');
    if (!agreeToTerms) {
      message.error('Please agree to the terms.');
      return;
    }

    // Debug: Log the raw form data
    console.log('🔍 Raw form data:', data);

    const { taxYear, ...rest } = data;

    const payload: {
      data: HealthCheckFormType;
      tax_year: number;
      application_id?: number;
    } = {
      data: rest,
      tax_year: data.taxYear,
    };

    // Debug: Log the payload before base64 encoding
    console.log('📦 Payload before base64 encoding:', payload);

    payload.data.dateOfBirth = window.btoa(
      dayjs(payload.data.dateOfBirth).format('YYYY-MM-DD'),
    );

    payload.data.australianTaxFileNumber = window.btoa(
      payload.data.australianTaxFileNumber,
    );

    // Debug: Log the final payload being sent to API
    console.log('🚀 Final payload sent to API:', payload);

    if (id) {
      payload.application_id = parseInt(id);
      putLodgeReturn(payload)
        .unwrap()
        .then((res) => {
          displaySuccess('Successfully submitted details.');
          setHealthCheckMeetingModalOpen(true);
        })
        .catch((err) => {
          displayError(err);
        });
    } else {
      postLodgeReturn(payload)
        .unwrap()
        .then((res) => {
          displaySuccess('Successfully submitted details.');
          setHealthCheckMeetingModalOpen(true);
        })
        .catch((err) => {
          displayError(err);
        });
    }
  };

  const applicationDetailData = applicationDetail?.data
    ?.data as HealthCheckFormType;

  useEffect(() => {
    if (profileData) {
      setValue('australianTaxFileNumber', profileData?.data?.tfnNumber ?? '');
    }

    if (applicationDetail) {
      setValue('firstName', applicationDetailData?.firstName);
      setValue('lastName', applicationDetailData?.lastName);
      setValue('email', applicationDetailData?.email);
      setValue('phone', applicationDetailData?.phone);
      setValue(
        'australianTaxFileNumber',
        applicationDetailData?.australianTaxFileNumber ??
          profileData?.data?.tax_file_number,
      );
      setValue('dateOfBirth', applicationDetailData.dateOfBirth);
    } else if (userData?.data) {
      setValue('firstName', userData.data.firstName ?? '');
      setValue('lastName', userData.data.lastName ?? '');
      setValue('email', userData.data.email ?? '');
      setValue('phone', userData.data.phone ?? '');
      setValue('country', userData.data.country ?? '');
    }
  }, [applicationDetail, userData, profileData, setValue]);

  useEffect(() => {
    if (propertyData?.data) {
      const australianProperties = propertyData.data.filter(
        (property: any) => property.propertyLocation === 'australian',
      );
      setAustralianPropertyData(australianProperties);
    }
  }, [propertyData]);

  const handleFormSubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };

  console.log('errors', errors, 'getValues', getValues());

  return (
    <StepperFormLayout
      showBackButton={false}
      handleSubmit={handleFormSubmit}
      nextBtnText="Submit Health Check"
      isSubmitLoading={postLodgeReturnLoading || putLodgeReturnLoading}
    >
      <Container width="md">
        <Row justify="center" style={{ marginTop: '1rem' }}>
          <Card
            style={{
              marginBottom: '0.5rem',
            }}
            title="Free ATO Tax Health Check"
          >
            <Col>
              <IntroContent />
              <form onSubmit={handleSubmit(onSubmit)}>
                <Row gutter={[8, 8]}>
                  <Col xs={24}>
                    <RadioGroupField
                      name="taxIncomeType"
                      label="Did you receieve any of the following Australian-sourced income this financial year?"
                      size="large"
                      multiple={true}
                      options={TAX_INCOME_OPTIONS}
                      control={control}
                      error={errors?.taxIncomeType?.message}
                    />
                  </Col>
                  <Col xs={24}>
                    <FormMainLabel label="Do you have any Australian properties?" />
                  </Col>
                  <Col xs={24} style={{ marginBottom: '1.5rem' }}>
                    {australianPropertyData?.map(
                      (property: any, index: number) => (
                        <div key={index}>
                          <PropertyItems
                            key={property.id}
                            property={property}
                            index={index}
                            selectedProperties={selectedProperties}
                            onSelect={handlePropertySelect}
                            refetchPropertyData={refetchPropertyData}
                            overseas={false}
                          />
                        </div>
                      ),
                    )}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                      }}
                    >
                      <Button
                        size="large"
                        onClick={() => {
                          setIsModalOpen(true);
                        }}
                      >
                        <PlusOutlined />
                        Add Property
                      </Button>
                    </div>
                  </Col>
                  <Col xs={24}>
                    <RadioGroupField
                      size="large"
                      name="mortgageReviewRequest"
                      label="Would you like a free mortgage health check to ensure you are on the lowest rates?"
                      options={AGREE_OPTIONS}
                      error={errors?.mortgageReviewRequest?.message ?? ''}
                      control={control}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <InputField
                      name="australianTaxFileNumber"
                      control={control}
                      placeholder="Enter your Australian Tax File Number"
                      type="password"
                      size="large"
                      label="Australian Tax File Number"
                      error={errors?.australianTaxFileNumber?.message}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <DatePickerField
                      control={control}
                      label="Date of Birth"
                      name="dateOfBirth"
                      size="large"
                      placeholder="Select Date of Birth"
                      error={errors?.dateOfBirth?.message}
                    />
                  </Col>
                  <Col xs={24}>
                    <Controller
                      name="agreeToTerms"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <div
                          style={{
                            padding: '16px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '8px',
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <Checkbox {...field} checked={field.value}>
                            <Typography.Text strong>
                              I authorise Odin Tax to act as my tax agent and
                              liaise with the ATO on my behalf.*
                            </Typography.Text>
                          </Checkbox>

                          <div
                            style={{ marginTop: '8px', paddingLeft: '24px' }}
                          >
                            <Typography.Text strong>
                              <strong>Disclaimer:</strong> We will add you to
                              our tax agent portal after submission. You can
                              request removal at any time.
                            </Typography.Text>
                          </div>
                        </div>
                      )}
                    />
                  </Col>
                  <Col xs={24}>
                    <Button
                      ref={submitButtonRef}
                      type="primary"
                      htmlType="submit"
                      style={{ display: 'none' }}
                      loading={postLodgeReturnLoading || putLodgeReturnLoading}
                    >
                      Submit
                    </Button>
                  </Col>
                </Row>
              </form>
            </Col>
          </Card>
        </Row>
      </Container>
      <PropertyFormModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        propertyOption="personal"
        onNewPropertyAdded={handleNewPropertyAdded}
      />
      <HealthCheckMeetingModal
        isOpen={healthCheckMeetingModalOpen}
        onClose={handleHealthCheckMeetingModalClose}
        onOk={handleHealthCheckMeetingModalOk}
      />
    </StepperFormLayout>
  );
};

export default HealthCheck;

const Label = ({ label }: { label: string }) => {
  return (
    <Typography.Title
      level={5}
      style={{
        color: 'rgba(0, 0, 0, 0.85)',
        marginTop: '1rem',
      }}
    >
      {label}
    </Typography.Title>
  );
};
